import './StickyTable.scss';
import cn from 'classnames';
import {
  JSX,
  ReactNode,
  Ref,
  SyntheticEvent,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useScrollbarSizes } from '../../utils/hooks/useDOMHelpers';
import { useVirtual as useVirtualize, Virtual } from '../../utils/hooks/useVirtual';
import { getFilledContentWidth, getScrollbarVisibility } from './sticky-table-utils';

const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 50;

type MouseEvent = (e: SyntheticEvent) => void;

export type CellRenderer<T> = ({
  data,
  rowData,
  classes,
  style,
  rowIndex,
  virtual,
  onMouseOut,
  onMouseOver
}: {
  data: T;
  rowData: object;
  classes: string[];
  style: object;
  rowIndex: number;
  columnIndex: number;
  virtual?: Virtual;
  onMouseOut?: MouseEvent;
  onMouseOver?: MouseEvent;
}) => JSX.Element;

interface TableCellProps<T> {
  data: T;
  rowData: object;
  classes?: string[];
  style?: object;
  rowIndex: number;
  columnIndex: number;
  columnWidth: number;
  rowHeight: number;
  renderer: CellRenderer<T>;
  hovered?: boolean;
  rowHoverClass?: string;
  setHoveredRow?: (i?: number) => void;
  virtual?: Virtual;
}

// Don't do much but memoize and run the render function.
const TableCell = <T,>({
  data,
  rowData,
  classes = [],
  style = {},
  rowIndex,
  columnIndex,
  columnWidth,
  rowHeight,
  renderer,
  hovered,
  rowHoverClass,
  setHoveredRow = () => {},
  virtual
}: TableCellProps<T>): JSX.Element => {
  const onMouseOver = useCallback(() => setHoveredRow(rowIndex), [setHoveredRow, rowIndex]);
  const onMouseOut = useCallback(() => setHoveredRow(), [setHoveredRow]);
  const hoverProps = useMemo(() => {
    const props: {
      onMouseOver?: MouseEvent;
      onMouseOut?: MouseEvent;
    } = {};
    if (rowHoverClass) {
      props.onMouseOver = onMouseOver;
      props.onMouseOut = onMouseOut;
    }
    return props;
  }, [rowHoverClass, onMouseOver, onMouseOut]);

  const resolvedClasses = useMemo(() => {
    const ourClasses = ['sticky-table-cell', ...classes];
    if (hovered && rowHoverClass) {
      ourClasses.push(rowHoverClass);
    }
    return ourClasses;
  }, [classes, hovered, rowHoverClass]);

  const resolvedStyle = useMemo(
    () => ({
      ...style,
      width: columnWidth,
      height: rowHeight
    }),
    [style, columnWidth, rowHeight]
  );

  return renderer({
    data,
    rowData,
    style: resolvedStyle,
    classes: resolvedClasses,
    rowIndex,
    columnIndex,
    virtual,
    ...hoverProps
  });
};

interface TableGridProps<T> {
  data: T[][];
  alternateColors: boolean;
  virtual: Virtual;
  renderer: CellRenderer<T>;
  rowHoverClass?: string;
  hoveredRow?: number;
  setHoveredRow: (i?: number) => void;
}

// This is the actual grid. It needs to do a 2d for loop
const TableGrid = <T,>({
  data,
  alternateColors,
  virtual,
  renderer,
  rowHoverClass,
  hoveredRow,
  setHoveredRow
}: TableGridProps<T>) => {
  const { cols, rows } = virtual;
  const items: ReactNode[] = [];
  // Only render items that our virtualization says we should
  for (let i = rows.visible.min; i <= rows.visible.max; ++i) {
    for (let j = cols.visible.min; j <= cols.visible.max; ++j) {
      const classes: string[] = [];
      if (alternateColors && i % 2) {
        classes.push('st-odd');
      }

      if (i === rows.visible.min) {
        classes.push('st-first-row');
      }

      if (i === rows.visible.max) {
        classes.push('st-last-row');
      }

      if (j === cols.visible.min) {
        classes.push('st-first-col');
      }

      if (j === cols.visible.max) {
        classes.push('st-last-col');
      }

      items.push(
        <TableCell
          key={`${i}_${j}`}
          classes={classes}
          renderer={renderer}
          data={data[i][j]}
          rowData={data[i]}
          rowIndex={i}
          columnIndex={j}
          columnWidth={cols.sizes[j]}
          rowHeight={rows.sizes[i]}
          style={{
            top: rows.positions[i],
            left: cols.positions[j]
          }}
          rowHoverClass={rowHoverClass}
          hovered={hoveredRow === i}
          setHoveredRow={setHoveredRow}
          virtual={virtual}
        />
      );
    }
  }
  return <div className='st-actual-grid'>{items}</div>;
};

interface TableHeaderProps<T, U = T> {
  data: T[];
  virtual: Virtual;
  renderer: CellRenderer<U>;
}
interface TableHeaderRowProps<T, U = T> extends TableHeaderProps<T, U> {
  className?: 'st-top-header' | 'st-bottom-header';
  leftWidth?: number;
  top?: number;
  height: number;
}

// Horizontal header bars. These take a 1d data prop and some offsets for positioning
const TableHeaderRow = <T,>({
  data,
  className,
  leftWidth = 0,
  top = 0,
  height,
  virtual,
  renderer
}: TableHeaderRowProps<T>) => {
  const { cols } = virtual;
  const { visible, sizes, positions } = cols;
  const width = leftWidth + sizes.reduce((a, b) => a + b, 0);
  const items: JSX.Element[] = [];
  for (let i = visible.min; i <= visible.max; ++i) {
    items.push(
      <TableCell
        key={i}
        renderer={renderer}
        data={data[i]}
        rowData={data}
        // Only one row!
        rowIndex={0}
        columnIndex={i}
        columnWidth={sizes[i]}
        rowHeight={height}
        style={{
          left: leftWidth + positions[i]
        }}
      />
    );
  }
  return (
    <div
      className={cn('sticky-header', className)}
      style={{
        height,
        top,
        width
      }}
    >
      {items}
    </div>
  );
};

export interface SuperTopItemType<T> {
  span: number;
  data: T;
}

export interface SuperTopInternalType<T> extends SuperTopItemType<T> {
  min: number;
  max: number;
  dataIndex: number;
}

type TableSuperHeaderRowProps<T> = TableHeaderRowProps<SuperTopInternalType<T>, T>;

const TableSuperHeaderRow = <T,>({
  data,
  className,
  leftWidth = 0,
  top = 0,
  height,
  virtual,
  renderer
}: TableSuperHeaderRowProps<T>) => {
  const { cols } = virtual;
  const { visible, sizes, positions } = cols;

  const seen: Record<number, boolean> = {};
  const items: JSX.Element[] = [];
  for (let i = visible.min; i <= visible.max; ++i) {
    const datum = data[i];
    // Keep track of the unique super header cells we've seen. If we have something with span 2,
    // we don't want to render it twice.
    if (datum == null || seen[datum.dataIndex]) {
      continue;
    }
    seen[datum.dataIndex] = true;
    let width = 0;
    for (let colIndex = datum.min; colIndex <= datum.max; ++colIndex) {
      width += sizes[colIndex];
    }
    items.push(
      <TableCell<T>
        key={i}
        renderer={renderer}
        data={datum.data}
        rowData={datum}
        // Only one row!
        rowIndex={0}
        columnIndex={i}
        columnWidth={width}
        rowHeight={height}
        style={{
          left: leftWidth + positions[datum.min]
        }}
      />
    );
  }
  return (
    <div
      className={cn('sticky-header', className)}
      style={{
        height,
        top
      }}
    >
      {items}
    </div>
  );
};

interface TableHeaderColProps<T> extends TableHeaderProps<T> {
  className: 'st-left-header' | 'st-right-header';
  left?: number;
  width: number;
  alternateColors?: boolean;
  rowHoverClass?: string;
  hoveredRow?: number;
  setHoveredRow?: (i?: number) => void;
}

// The vertical headers. These also only take 1d arrays for data
const TableHeaderCol = <T,>({
  data,
  alternateColors,
  className,
  left = 0,
  width,
  virtual,
  renderer,
  rowHoverClass,
  hoveredRow,
  setHoveredRow
}: TableHeaderColProps<T>) => {
  const { rows } = virtual;
  const { visible, sizes, positions } = rows;

  const items: JSX.Element[] = [];
  for (let i = visible.min; i <= visible.max; ++i) {
    const classes: string[] = [];
    if (alternateColors && i % 2) {
      classes.push('st-odd');
    }
    items.push(
      <TableCell
        key={i}
        classes={classes}
        renderer={renderer}
        data={data[i]}
        rowData={data}
        rowIndex={i}
        columnIndex={0}
        columnWidth={width}
        rowHeight={sizes[i]}
        style={{
          top: positions[i]
        }}
        rowHoverClass={rowHoverClass}
        hovered={hoveredRow === i}
        setHoveredRow={setHoveredRow}
        virtual={virtual}
      />
    );
  }
  return (
    <div
      className={cn('sticky-header', className)}
      style={{
        width,
        left
      }}
    >
      {items}
    </div>
  );
};

export type CornerLocation = 'nw' | 'ne' | 'sw' | 'se';

interface CornerProps {
  className: 'st-nw' | 'st-ne' | 'st-sw' | 'st-se';
  children?: ReactNode;
  width: number;
  height: number;
  top: number;
  left: number;
  outerWidth?: boolean;
}

// These corners have z-index: 2 and sit above the headers. To get properly synchronized
// positioning, they also need "position: sticky". However, if they have a width or height, they'll
// offset the headers, kind of defeating the purpose. We can get around this by actually giving them
// a height/width of 0, giving them "overflow: visible", and sizing the sub-divs. The exception is
// some hackery for the right-side corners: they need to actually have a width to appear properly,
// so we take a flag to determine if the outer div should have the same width.
const Corner = ({
  outerWidth = false,
  className,
  width,
  height,
  top,
  left,
  children
}: CornerProps): JSX.Element => (
  <div className='st-corner' style={{ top, left, width: outerWidth ? width : 0 }}>
    <div
      className={cn('st-corner-inner', className)}
      style={{
        width,
        height
      }}
    >
      {children}
    </div>
  </div>
);

// Basic cell renderer. If someone wants to provide a custom function but only wants to change it
// slightly from the default, they can use a wrapper around this.
export const basicRenderer: CellRenderer<unknown> = ({
  data,
  style = {},
  classes = [],
  onMouseOut,
  onMouseOver
}) => {
  const hoverProps: { onMouseOut?: MouseEvent; onMouseOver?: MouseEvent } = {};
  if (onMouseOut) {
    hoverProps.onMouseOut = onMouseOut;
  }
  if (onMouseOver) {
    hoverProps.onMouseOver = onMouseOver;
  }
  return (
    <div style={style} className={cn(classes)} {...hoverProps}>
      {data as ReactNode}
    </div>
  );
};

export const textCellRenderer: CellRenderer<string> = ({ classes = [], ...rest }) =>
  basicRenderer({ classes: ['sticky-table-text-cell', ...classes], ...rest });

type ScrollTo = (row: number, col: number) => void;
interface StickyTableProps<
  TableDataType,
  TopDataType,
  LeftDataType,
  BottomDataType,
  RightDataType,
  SuperTopDataType
> {
  data: TableDataType[][];
  width: number;
  height: number;
  topData?: TopDataType[];
  leftData?: LeftDataType[];
  bottomData?: BottomDataType[];
  rightData?: RightDataType[];
  superTopData?: SuperTopItemType<SuperTopDataType>[];
  alternateColors?: boolean;
  columnWidth?: number | ((index: number) => number);
  rowHeight?: number | ((index: number) => number);
  topHeight?: number;
  rightWidth?: number;
  bottomHeight?: number;
  leftWidth?: number;
  superTopHeight?: number;
  cellRenderer?: CellRenderer<TableDataType>;
  topRenderer?: CellRenderer<TopDataType>;
  rightRenderer?: CellRenderer<RightDataType>;
  bottomRenderer?: CellRenderer<BottomDataType>;
  leftRenderer?: CellRenderer<LeftDataType>;
  superTopRenderer?: CellRenderer<SuperTopDataType>;
  cornerRenderer?: (location: CornerLocation) => JSX.Element;
  overscan?: number;
  rowHoverClass?: string;
  scrollToRef?: Ref<ScrollTo>;
  fillContainer?: boolean;
}

// This is where the magic happens!
export const StickyTable = <
  TableDataType,
  TopDataType,
  LeftDataType,
  BottomDataType,
  RightDataType,
  SuperTopDataType
>({
  topData,
  rightData,
  bottomData,
  leftData,
  // TODO: we need to add this for left/right/bottom
  superTopData,
  data,
  width,
  height,
  alternateColors = true,
  columnWidth = DEFAULT_WIDTH,
  rowHeight = DEFAULT_HEIGHT,
  topHeight: topHeightProp,
  rightWidth: rightWidthProp,
  bottomHeight: bottomHeightProp,
  leftWidth: leftWidthProp,
  superTopHeight: superTopHeightProp,
  cellRenderer = basicRenderer,
  topRenderer = basicRenderer,
  rightRenderer = basicRenderer,
  bottomRenderer = basicRenderer,
  leftRenderer = basicRenderer,
  superTopRenderer = basicRenderer,
  cornerRenderer,
  overscan = 10,
  // If set, this className will be applied all cells in a row that's hovered over
  rowHoverClass,
  scrollToRef,
  fillContainer
}: StickyTableProps<
  TableDataType,
  TopDataType,
  LeftDataType,
  BottomDataType,
  RightDataType,
  SuperTopDataType
>): JSX.Element => {
  // This goes on the outermost div. We just use it for the scrollbar hook.
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollbarSizes = useScrollbarSizes(containerRef);
  const columnCount = data[0]?.length || topData?.length || bottomData?.length || 0;
  const rowCount = data.length || 0;

  const [topHeight, rightWidth, bottomHeight, leftWidth, superTopHeight] = useMemo(() => {
    let resolvedTopHeight = 0;
    let resolvedRightWidth = 0;
    let resolvedBottomHeight = 0;
    let resolvedLeftWidth = 0;
    let resolvedSuperTopHeight = 0;
    if (topData) {
      if (topHeightProp) {
        resolvedTopHeight = topHeightProp;
      } else if (typeof rowHeight !== 'function') {
        resolvedTopHeight = rowHeight;
      } else {
        resolvedTopHeight = DEFAULT_HEIGHT;
      }
    }
    if (rightData) {
      if (rightWidthProp) {
        resolvedRightWidth = rightWidthProp;
      } else if (typeof columnWidth !== 'function') {
        resolvedRightWidth = columnWidth;
      } else {
        resolvedRightWidth = DEFAULT_WIDTH;
      }
    }

    if (bottomData) {
      if (bottomHeightProp) {
        resolvedBottomHeight = bottomHeightProp;
      } else if (typeof rowHeight !== 'function') {
        resolvedBottomHeight = rowHeight;
      } else {
        resolvedBottomHeight = DEFAULT_HEIGHT;
      }
    }

    if (leftData) {
      if (leftWidthProp) {
        resolvedLeftWidth = leftWidthProp;
      } else if (typeof columnWidth !== 'function') {
        resolvedLeftWidth = columnWidth;
      } else {
        resolvedLeftWidth = DEFAULT_WIDTH;
      }
    }

    if (superTopData) {
      if (superTopHeightProp) {
        resolvedSuperTopHeight = superTopHeightProp;
      } else if (typeof rowHeight !== 'function') {
        resolvedSuperTopHeight = rowHeight;
      } else {
        resolvedSuperTopHeight = DEFAULT_HEIGHT;
      }
    }

    return [
      resolvedTopHeight,
      resolvedRightWidth,
      resolvedBottomHeight,
      resolvedLeftWidth,
      resolvedSuperTopHeight
    ];
  }, [
    topData,
    topHeightProp,
    rightData,
    rightWidthProp,
    bottomData,
    bottomHeightProp,
    leftData,
    leftWidthProp,
    superTopData,
    superTopHeightProp,
    rowHeight,
    columnWidth
  ]);

  const superTopList = useMemo(() => {
    if (!superTopData) {
      return null;
    }
    let colIndex = 0;
    const superList: SuperTopInternalType<SuperTopDataType>[] = new Array(columnCount);
    for (let superIndex = 0; superIndex < superTopData.length; ++superIndex) {
      const datum = superTopData[superIndex];
      const { span } = datum;
      for (let i = colIndex; i < colIndex + span; ++i) {
        superList[i] = {
          ...datum,
          min: colIndex,
          max: colIndex + span - 1,
          dataIndex: superIndex
        };
      }
      colIndex += span;
    }
    return superList;
  }, [superTopData, columnCount]);

  // Size of the actual grid. The grid table will need to have these dimensions. Because we have
  // custom size functions, we have to iterate over the entire data object and aggregate the
  // dimensions. For large data sets this can be expensive. This only needs to be re-computed when
  // the sizes or data change, so memoize!
  const [contentWidth, contentHeight] = useMemo(() => {
    let calcWidth = 0;
    let calcHeight = 0;
    if (typeof columnWidth === 'function') {
      for (let i = 0; i < columnCount; ++i) {
        calcWidth += columnWidth(i);
      }
    } else {
      calcWidth = columnWidth * columnCount;
    }

    if (typeof rowHeight === 'function') {
      for (let i = 0; i < rowCount; ++i) {
        calcHeight += rowHeight(i);
      }
    } else {
      calcHeight = rowHeight * rowCount;
    }
    const remainingHeight = height - topHeight - bottomHeight - superTopHeight;
    if (fillContainer && calcHeight < remainingHeight) {
      calcHeight = remainingHeight - scrollbarSizes.height;
    }
    // Unlike height, a table narrower than its container always fills its width; it reads as
    // unfinished otherwise. getFilledContentWidth decides how much room the scrollbar gets.
    const remainingWidth = width - leftWidth - rightWidth;
    calcWidth = getFilledContentWidth({
      contentWidth: calcWidth,
      contentHeight: calcHeight,
      availableWidth: remainingWidth,
      availableHeight: remainingHeight,
      scrollbarWidth: scrollbarSizes.width,
      fillContainer
    });
    return [calcWidth, calcHeight];
  }, [
    columnWidth,
    rowHeight,
    height,
    topHeight,
    bottomHeight,
    superTopHeight,
    fillContainer,
    width,
    leftWidth,
    rightWidth,
    columnCount,
    rowCount,
    scrollbarSizes.height,
    scrollbarSizes.width
  ]);

  const [
    viewWidth,
    viewHeight,
    rightLeftPos,
    bottomTopPos,
    tableHasVertScroll,
    tableHasHorizScroll
  ] = useMemo(() => {
    // Size of the grid plus all the headers. The containing div will need to have these
    // dimensions
    const totalWidth = leftWidth + rightWidth + contentWidth;
    const totalHeight = topHeight + bottomHeight + superTopHeight + contentHeight;

    let calcViewWidth = totalWidth > width ? width : totalWidth;

    const { hasHorizScroll, hasVertScroll } = getScrollbarVisibility({
      totalWidth,
      totalHeight,
      width,
      height,
      scrollbarSizes,
      fillContainer
    });
    let calcViewHeight = hasVertScroll ? height : totalHeight;

    // The position from the left things like the right table/border/corners will be. This will be
    // the size of the view minus the size of the right header.
    let calcRightLeftPos = calcViewWidth - rightWidth;
    let calcBottomTopPos = calcViewHeight - bottomHeight;

    // If there are scroll bars, we're going to need to tweak some sizing and positioning.
    if (hasVertScroll || hasHorizScroll) {
      // If there are scroll bars on both sides, we need to move all our headers, corners, and
      // fake borders over the size of the scroll bars. If there is only one scroll bar, the side
      // that doesn't have one is smaller than the box. Thus the view was shrunk to only be the
      // full size of the content. However, that content is now being pushed a little by the
      // scroll bar in the other direction, requiring us to scroll that tiny amount. So expand
      // that width
      if (hasVertScroll && hasHorizScroll) {
        calcRightLeftPos -= Math.max(scrollbarSizes.width, width - calcViewWidth);
        calcBottomTopPos -= scrollbarSizes.height;
      } else if (hasVertScroll) {
        calcViewWidth += scrollbarSizes.width;
      } else {
        calcViewHeight += scrollbarSizes.height;
      }
    }
    if (fillContainer) {
      calcBottomTopPos = height - bottomHeight;
      calcViewHeight = height + (hasHorizScroll ? scrollbarSizes.height : 0);
      calcViewWidth = width + (hasVertScroll ? scrollbarSizes.width : 0);
    }
    return [
      calcViewWidth,
      calcViewHeight,
      calcRightLeftPos,
      calcBottomTopPos,
      hasVertScroll,
      hasHorizScroll
    ];
  }, [
    leftWidth,
    rightWidth,
    contentWidth,
    topHeight,
    bottomHeight,
    superTopHeight,
    contentHeight,
    height,
    width,
    scrollbarSizes,
    fillContainer
  ]);

  // Given all relevant data, get virtual information. This includes
  // - Minimum and maximum rows/columns that must be rendered to populate the viewable box (the
  //   ranges that, if you're looking at an element outside that range, you don't have to render
  //   it).
  // - Dimensions of elements
  // - Absolute positions of elements
  // In order to get the virtualization (min/max) information, we have to compute the positions,
  // and in order to compute the positions, we need the sizes. Thus, we just handle and return all
  // this information so we don't have to repeat the work. This takes overscanning into account.
  // Since this is the only thing that is hooking into the scroll events on the div, we provide
  // our own setters and keep the state internally.
  const [virtual, setLeft, setTop] = useVirtualize({
    overscan,
    columnCount,
    rowCount,
    columnWidth,
    rowHeight,
    width: viewWidth - leftWidth - rightWidth,
    height: viewHeight - topHeight - bottomHeight - superTopHeight
  });

  useImperativeHandle(
    scrollToRef,
    () => (row: number, col: number) => {
      const xPos = virtual.cols.positions[col] || 0;
      const yPos = virtual.rows.positions[row] || 0;
      containerRef.current?.scrollTo(xPos, yPos);
    },
    [virtual]
  );

  const [hoveredRow, setHoveredRow] = useState<number>();

  // `.sticky-table`'s own `overscroll-behavior: none` (StickyTable.scss) exists to stop the page
  // from also scrolling once a genuinely-scrollable table reaches its own top/bottom -- but a
  // table with nothing to scroll is permanently "at its boundary", so a static `none` there
  // unconditionally swallows every wheel event over it instead of letting the gesture chain to
  // the page (confirmed live: forcing overscroll-behavior to 'auto' on such a table immediately
  // restored page scroll). tableHasVertScroll/tableHasHorizScroll (computed above) already tell
  // us exactly when the table has real scrollable range of its own -- only apply `none` then.
  // Set independently per axis (not the `overscrollBehavior` shorthand, which sets both at once):
  // a table scrollable in only one direction still has zero range in the other, and the shorthand
  // would swallow wheel gestures on that orthogonal axis too -- the same bug, just single-axis.

  return (
    <div
      className='sticky-table-outer'
      style={{
        width,
        height: viewHeight
      }}
    >
      <div
        ref={containerRef}
        className='sticky-table'
        style={{
          width: viewWidth,
          height: viewHeight,
          overscrollBehaviorX: tableHasHorizScroll ? 'none' : 'auto',
          overscrollBehaviorY: tableHasVertScroll ? 'none' : 'auto'
        }}
        onScroll={e => {
          setLeft(e.currentTarget.scrollLeft);
          setTop(e.currentTarget.scrollTop);
        }}
      >
        {(topData || superTopData) && leftData && (
          <Corner
            className='st-nw'
            width={leftWidth}
            height={topHeight + superTopHeight}
            top={0}
            left={0}
          >
            {cornerRenderer ? cornerRenderer('nw') : null}
          </Corner>
        )}
        {bottomData && leftData && (
          <Corner
            className='st-sw'
            width={leftWidth}
            height={bottomHeight}
            top={bottomTopPos}
            left={0}
          >
            {cornerRenderer ? cornerRenderer('sw') : null}
          </Corner>
        )}
        {(topData || superTopData) && rightData && (
          <Corner
            className='st-ne'
            outerWidth
            width={rightWidth}
            height={topHeight + superTopHeight}
            top={0}
            left={rightLeftPos}
          >
            {cornerRenderer ? cornerRenderer('ne') : null}
          </Corner>
        )}
        {bottomData && rightData && (
          <Corner
            className='st-se'
            outerWidth
            width={rightWidth}
            height={bottomHeight}
            top={bottomTopPos}
            left={rightLeftPos}
          >
            {cornerRenderer ? cornerRenderer('se') : null}
          </Corner>
        )}
        {superTopList && (
          <TableSuperHeaderRow
            data={superTopList}
            leftWidth={leftWidth}
            height={superTopHeight}
            virtual={virtual}
            renderer={superTopRenderer}
          />
        )}
        {topData && (
          <TableHeaderRow<TopDataType>
            data={topData}
            className='st-top-header'
            leftWidth={leftWidth}
            top={superTopHeight}
            height={topHeight}
            virtual={virtual}
            renderer={topRenderer}
          />
        )}
        {leftData && (
          <TableHeaderCol<LeftDataType>
            data={leftData}
            className='st-left-header'
            width={leftWidth}
            virtual={virtual}
            renderer={leftRenderer}
            rowHoverClass={rowHoverClass}
            hoveredRow={hoveredRow}
            setHoveredRow={setHoveredRow}
            alternateColors={alternateColors}
          />
        )}
        {rightData && (
          <TableHeaderCol<RightDataType>
            data={rightData}
            className='st-right-header'
            left={rightLeftPos}
            width={rightWidth}
            virtual={virtual}
            renderer={rightRenderer}
            rowHoverClass={rowHoverClass}
            hoveredRow={hoveredRow}
            setHoveredRow={setHoveredRow}
            alternateColors={alternateColors}
          />
        )}
        {bottomData && (
          <TableHeaderRow<BottomDataType>
            data={bottomData}
            className='st-bottom-header'
            top={bottomTopPos}
            leftWidth={leftWidth}
            height={bottomHeight}
            virtual={virtual}
            renderer={bottomRenderer}
          />
        )}
        <div
          className='st-inner-grid'
          style={{
            width: contentWidth + rightWidth,
            maxWidth: contentWidth + rightWidth,
            height: contentHeight,
            maxHeight: contentHeight,
            top: -bottomHeight,
            left: leftWidth
          }}
        >
          <TableGrid<TableDataType>
            data={data}
            alternateColors={alternateColors}
            virtual={virtual}
            renderer={cellRenderer}
            rowHoverClass={rowHoverClass}
            hoveredRow={hoveredRow}
            setHoveredRow={setHoveredRow}
          />
        </div>
      </div>
      {
        // The vertical scrollbar is a decoration of the whole scrolling box, so it runs the full
        // height of `.sticky-table` — including alongside the sticky header/footer rows, which never
        // actually scroll. These masks sit in the non-scrolling outer wrapper (a separate stacking
        // context) so they can paint over that stretch of the scrollbar and make it look confined to
        // the rows that actually move.
        tableHasVertScroll && (topData || superTopData) && (
          <div
            className='st-scrollbar-mask st-scrollbar-mask-top'
            style={{
              top: 0,
              left: viewWidth - scrollbarSizes.width,
              width: scrollbarSizes.width,
              height: topHeight + superTopHeight
            }}
          />
        )
      }
      {tableHasVertScroll && bottomData && (
        <div
          className='st-scrollbar-mask st-scrollbar-mask-bottom'
          style={{
            bottom: 0,
            left: viewWidth - scrollbarSizes.width,
            width: scrollbarSizes.width,
            height: bottomHeight
          }}
        />
      )}
    </div>
  );
};

// eslint-disable-next-line no-restricted-syntax
export default StickyTable;
